import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AnnotationPlugin from 'chartjs-plugin-annotation';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-gas-cost-per-smc-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './gas-cost-per-smc-chart.component.html',
  styleUrl: './gas-cost-per-smc-chart.component.css'
})
export class GasCostPerSmcChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  //marketAverage: number = 0.35;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummytransparentBaseline: number[] = [0, 0.43, 0.47, 0.49, 0.55, 0.57, 0];
  dummystepsData: number[] = [0.43, 0.04, 0.02, 0.06, 0.02, 0.03, 0.6];

  baselineLabel: string = "Baseline";
  costPerSmcLabel: string = "Cost per Smc";

  stepsLabel: string[] = ['Previous Period', 'Fixed Quota', 'Spread', 'TMS', 'Current Period'];
  numberOfSteps: number = 5;

  transparentBaseline: number[] = [0, 0.43, 0.47, 0.49, 0];
  stepsData: number[] = [0.43, 0.04, 0.02, 0.06, 0.55];

  @Input() materialCostsDifferenceSign: string = '+';
  @Input() materialCostsDifference: number = 0;

  @Input() fixedCostsDifferenceSign: string = '+';
  @Input() fixedCostsDifference: number = 0;

  @Input() tmsDifferenceSign: string = '+';
  @Input() tmsDifference: number = 0;

  @Input() additionalChargesDifferenceSign: string = '+';
  @Input() additionalChargesDifference: number = 0;

  @Input() taxesDifferenceSign: string = '+';
  @Input() taxesDifference: number = 0;

  @Input() previousPeriodCostPerSmc: number = 0;
  @Input() currentPeriodCostPerSmc: number = 0;

  @Input() inputedPeriods: string[] | undefined = undefined;

  prevPeriod: string = 'Previous Period';
  currPeriod: string = 'Current Period';

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();
    combineLatest([
      this.translocoService.selectTranslate('dashboard.baseline'),
      this.translocoService.selectTranslate('dashboard.cost_per_smc')
    ]).subscribe(([baselineTranslation, costPerSmcTranslation]) => {
      this.baselineLabel = baselineTranslation;
      this.costPerSmcLabel = costPerSmcTranslation;
    });

    combineLatest([
      this.translocoService.selectTranslate('dashboard.period.previous'),
      this.translocoService.selectTranslate('dashboard.costs_per_smc_labels.material_costs'),
      this.translocoService.selectTranslate('dashboard.costs_per_smc_labels.fixed_costs'),
      this.translocoService.selectTranslate('dashboard.costs_per_smc_labels.tms'),
      this.translocoService.selectTranslate('dashboard.costs_per_smc_labels.additional_charges'),
      this.translocoService.selectTranslate('dashboard.costs_per_smc_labels.taxes'),
      this.translocoService.selectTranslate('dashboard.period.current')
    ]).subscribe(([prevPeriod, material, fixed, tms, additionalCharges, taxesTranslation, currPeriod]) => {
      this.prevPeriod = prevPeriod;
      this.currPeriod = currPeriod;

      const previous = this.inputedPeriods?.[0] || prevPeriod;
      const current = this.inputedPeriods?.[1] || currPeriod;

      this.stepsLabel = [previous, material, fixed, tms, additionalCharges, taxesTranslation, current];
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.transparentBaseline = this.dummytransparentBaseline;
      this.stepsData = this.dummystepsData;
    }
    else {
      this.transparentBaseline =
        [
          0,
          this.previousPeriodCostPerSmc,
          (this.previousPeriodCostPerSmc + this.materialCostsDifference),
          (this.previousPeriodCostPerSmc + this.materialCostsDifference + this.fixedCostsDifference),
          (this.previousPeriodCostPerSmc + this.materialCostsDifference + this.fixedCostsDifference + this.tmsDifference),
          (this.previousPeriodCostPerSmc + this.materialCostsDifference + this.fixedCostsDifference + this.tmsDifference + this.additionalChargesDifference),
          (this.previousPeriodCostPerSmc + this.materialCostsDifference + this.fixedCostsDifference + this.tmsDifference + this.additionalChargesDifference + this.taxesDifference),
          0];
      this.stepsData = [this.previousPeriodCostPerSmc, this.materialCostsDifference, this.fixedCostsDifference, this.tmsDifference, this.additionalChargesDifference, this.taxesDifference, this.currentPeriodCostPerSmc]

      this.stepsLabel[0] = this.inputedPeriods?.[0] || this.prevPeriod;
      this.stepsLabel[6] = this.inputedPeriods?.[1] || this.currPeriod;
    }

    Chart.register(ChartDataLabels, AnnotationPlugin);

    this.data = {
      labels: this.stepsLabel,
      datasets: [
        {
          type: 'bar',
          label: this.baselineLabel,
          data: this.transparentBaseline,
          backgroundColor: 'transparent',
          borderWidth: 0,
          stack: 'combined',
          datalabels: {
            display: false,
          },
        },
        {
          type: 'bar',
          label: this.costPerSmcLabel,
          data: this.stepsData,
          backgroundColor: ['#CBDCD6', '#CBDCD6', '#CBDCD6', '#CBDCD6', '#CBDCD6', '#CBDCD6', '#7EA4A4'],
          borderRadius: 12,
          borderWidth: 1,
          datalabels: {
            display: true,
            align: 'center',
            anchor: 'center',
            color: '#000',
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            formatter: (value: number, context: any) => {
              const formatDifference = (sign: string, value: number) => {
                return value === 0 ? '' : `${sign} ${this.decimalPipe.transform(value, '1.2-2') || value.toFixed(2)}€`;
              };

              const diffs = [
                this.previousPeriodCostPerSmc === 0 ? '' : this.decimalPipe.transform(this.previousPeriodCostPerSmc, '1.2-2') || this.previousPeriodCostPerSmc.toFixed(2),
                formatDifference(this.materialCostsDifferenceSign, this.materialCostsDifference),
                formatDifference(this.fixedCostsDifferenceSign, this.fixedCostsDifference),
                formatDifference(this.tmsDifferenceSign, this.tmsDifference),
                formatDifference(this.additionalChargesDifferenceSign, this.additionalChargesDifference),
                formatDifference(this.taxesDifferenceSign, this.taxesDifference),
                this.currentPeriodCostPerSmc === 0 ? '' : this.decimalPipe.transform(this.currentPeriodCostPerSmc, '1.2-2') || this.currentPeriodCostPerSmc.toFixed(2),
              ];
              return diffs[context.dataIndex];
            },
          },
          stack: 'combined'
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        /*annotation: {
          annotations: {
            marketAverage: {
              type: 'line',
              yMin: this.marketAverage,
              yMax: this.marketAverage,
              xMin: 3.5,
              borderColor: '#FFA500',
              borderDash: [5, 5],
              borderWidth: 2,

            },
          },
        },*/
        tooltip: {
          callbacks: {
            label: (tooltipItem: any) => {
              const index = tooltipItem.dataIndex;

              const formatDifference = (sign: string, value: number) => {
                return value === 0 ? '' : `${sign} ${this.decimalPipe.transform(value, '1.2-2') || value.toFixed(2)}€`;
              };

              const diffs = [
                this.previousPeriodCostPerSmc === 0 ? '' : this.decimalPipe.transform(this.previousPeriodCostPerSmc, '1.2-2') || this.previousPeriodCostPerSmc.toFixed(2),
                formatDifference(this.materialCostsDifferenceSign, this.materialCostsDifference),
                formatDifference(this.fixedCostsDifferenceSign, this.fixedCostsDifference),
                formatDifference(this.tmsDifferenceSign, this.tmsDifference),
                formatDifference(this.additionalChargesDifferenceSign, this.additionalChargesDifference),
                formatDifference(this.taxesDifferenceSign, this.taxesDifference),
                this.currentPeriodCostPerSmc === 0 ? '' : this.decimalPipe.transform(this.currentPeriodCostPerSmc, '1.2-2') || this.currentPeriodCostPerSmc.toFixed(2),
              ];

              return diffs[index] ? `${this.stepsLabel[index]}: ${diffs[index]}` : '';
            },
          },
        },

      },
      scales: {
        x: {
          stacked: true,
          title: {
            display: false,
          },
          ticks: {
            display: true,
          },
        },
        y: {
          beginAtZero: true,
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
        },
      },
    };
  }
}
